import { LitElement, html, PropertyValues } from 'lit';
import { RADIO_EVENT } from './radio-group.constants';
import styles from './radio-button.styles';

export interface RadioProps {
	value: string;
	disabled?: boolean;
	checked?: boolean;
}

export class Radio extends LitElement {
	static override styles = [styles()];

	static override get properties() {
		return {
			value: { type: String },
			disabled: { type: Boolean, reflect: true },
			checked: { type: Boolean, reflect: true },
		};
	}

	/**
	 * Value of the button, raised with the event.
	 */
	declare value: string;

	/**
	 * Should the button be disabled.
	 */
	declare disabled: boolean;

	/**
	 * The button initial state
	 */
	declare checked: boolean;

	constructor() {
		super();
		this.disabled = false;
		this.checked = false;
	}

	override connectedCallback(): void {
		super.connectedCallback();
		this.setAttribute('role', 'radio');
		this.addEventListener('click', this.#handleClick);
	}

	protected override updated(changedProperties: PropertyValues<this>): void {
		super.updated(changedProperties);

		if (changedProperties.has('disabled')) {
			this.#setDisabled();
		}

		if (changedProperties.has('checked')) {
			this.#setChecked();
		}
	}

	override render() {
		return html`
			<button
				type="button"
				class="radio-button"
				role="radio"
				?disabled=${this.disabled}
				aria-checked=${this.checked}
			>
				<span class="radio-input"></span>
				<slot name="leading"></slot>
				<slot></slot>
			</button>
		`;
	}

	#handleClick = (event: MouseEvent) => {
		if (this.disabled) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		const options = {
			detail: { value: this.value, type: 'select' },
			bubbles: true,
			composed: false,
		};

		this.dispatchEvent(new CustomEvent(RADIO_EVENT, options));
	};

	#setDisabled = () => {
		this.setAttribute('aria-disabled', String(this.disabled));
	};

	#setChecked = () => {
		this.setAttribute('aria-checked', String(this.checked));
	};
}

window.customElements.define('m-radio', Radio);

declare global {
	interface HTMLElementTagNameMap {
		'm-radio': Radio;
	}
}
